<template>
  <header
    class="sn"
    :class="{ 'is-transparent': transparente, 'is-compact': compacta, 'is-open': menuAbierto }"
  >
    <div class="sn-inner">
      <!-- Logo -->
      <router-link :to="{ name: 'inicio' }" class="sn-brand" :aria-label="`${$root.constantes.nombre_sitio} — Inicio`">
        <img
          class="sn-logo"
          :src="logoClaro ? require('@/assets/img/logo_santo_domingo_dark.png') : require('@/assets/img/logo_santo_domingo.png')"
          :alt="$root.constantes.logo_nombre_sitio"
          width="425"
          height="106"
        />
      </router-link>

      <!-- Menú de escritorio -->
      <nav class="sn-nav" aria-label="Menú principal">
        <ul class="sn-links">
          <li v-for="item in enlaces" :key="item.name">
            <router-link :to="{ name: item.name }" class="sn-link" :class="{ 'is-active': activo(item.name) }">
              {{ item.texto }}
            </router-link>
          </li>

          <!-- Desplegables: Parroquia y Cáritas -->
          <li
            v-for="grupo in desplegables"
            :key="grupo.clave"
            class="sn-drop"
            @mouseenter="abrirDesplegable(grupo.clave)"
            @mouseleave="cerrarDesplegableConRetardo"
          >
            <button
              type="button"
              class="sn-link"
              :class="{ 'is-active': grupo.items.some((i) => i.name && activo(i.name)) }"
              aria-haspopup="true"
              :aria-expanded="desplegable === grupo.clave"
              @click="desplegable = desplegable === grupo.clave ? null : grupo.clave"
              @keydown.esc="desplegable = null"
            >
              {{ grupo.texto }}
              <span class="material-symbols-outlined sn-caret">expand_more</span>
            </button>
            <transition name="sn-pop">
              <div v-show="desplegable === grupo.clave" class="sn-menu" role="menu">
                <template v-for="item in grupo.items">
                  <router-link v-if="item.name" :key="item.name" :to="{ name: item.name }" class="sn-menu-item" role="menuitem">
                    <span class="material-symbols-outlined">{{ item.icon }}</span>
                    <span>
                      <strong>{{ item.texto }}</strong>
                      <small>{{ item.detalle }}</small>
                    </span>
                  </router-link>
                  <a v-else :key="item.href" :href="item.href" target="_blank" rel="noopener" class="sn-menu-item" role="menuitem">
                    <span class="material-symbols-outlined">{{ item.icon }}</span>
                    <span>
                      <strong>{{ item.texto }}</strong>
                      <small>{{ item.detalle }}</small>
                    </span>
                  </a>
                </template>
              </div>
            </transition>
          </li>

          <li>
            <button v-track:abrir_modal="{ modal: 'lecturas', origen: 'menu' }" type="button" class="sn-link" @click="$bvModal.show('evangelioDia')">Lecturas</button>
          </li>
        </ul>
      </nav>

      <!-- Acciones -->
      <div class="sn-actions">
        <button v-track:abrir_modal="{ modal: 'contacto', origen: 'menu' }" type="button" class="sn-contact" @click="$bvModal.show('contact')">
          <span class="material-symbols-outlined">mail</span>
          <span>Contacto</span>
        </button>
        <button
          type="button"
          class="sn-icon"
          :aria-label="$store.getters.isDarkMode ? 'Activar modo claro' : 'Activar modo oscuro'"
          :title="$store.getters.isDarkMode ? 'Modo claro' : 'Modo oscuro'"
          @click="toggleDark"
        >
          <span class="material-symbols-outlined">{{ $store.getters.isDarkMode ? "light_mode" : "dark_mode" }}</span>
        </button>
        <button
          type="button"
          class="sn-icon sn-burger"
          :aria-expanded="menuAbierto"
          aria-controls="sn-panel"
          :aria-label="menuAbierto ? 'Cerrar menú' : 'Abrir menú'"
          @click="menuAbierto = !menuAbierto"
        >
          <span class="material-symbols-outlined">{{ menuAbierto ? "close" : "menu" }}</span>
        </button>
      </div>
    </div>

    <!-- Menú móvil -->
    <transition name="sn-fade">
      <div v-if="menuAbierto" class="sn-overlay" @click="menuAbierto = false"></div>
    </transition>
    <transition name="sn-slide">
      <nav v-if="menuAbierto" id="sn-panel" class="sn-panel" aria-label="Menú">
        <ul class="sn-panel-links">
          <li>
            <router-link :to="{ name: 'inicio' }" class="sn-panel-link" :class="{ 'is-active': activo('inicio') }">
              <span class="material-symbols-outlined">home</span> Inicio
            </router-link>
          </li>
          <li v-for="item in enlaces" :key="'m-' + item.name">
            <router-link :to="{ name: item.name }" class="sn-panel-link" :class="{ 'is-active': activo(item.name) }">
              <span class="material-symbols-outlined">{{ item.icon }}</span> {{ item.texto }}
            </router-link>
          </li>
          <template v-for="grupo in desplegables">
            <li :key="'t-' + grupo.clave" class="sn-panel-group">{{ grupo.texto }}</li>
            <li v-for="item in grupo.items" :key="'m-' + (item.name || item.href)">
              <router-link v-if="item.name" :to="{ name: item.name }" class="sn-panel-link" :class="{ 'is-active': activo(item.name) }">
                <span class="material-symbols-outlined">{{ item.icon }}</span> {{ item.texto }}
              </router-link>
              <a v-else :href="item.href" target="_blank" rel="noopener" class="sn-panel-link">
                <span class="material-symbols-outlined">{{ item.icon }}</span> {{ item.texto }}
              </a>
            </li>
          </template>
          <li>
            <button v-track:abrir_modal="{ modal: 'lecturas', origen: 'menu_movil' }" type="button" class="sn-panel-link" @click="abrirModal('evangelioDia')">
              <span class="material-symbols-outlined">menu_book</span> Lecturas del día
            </button>
          </li>
          <li>
            <button v-track:abrir_modal="{ modal: 'contacto', origen: 'menu_movil' }" type="button" class="sn-panel-link" @click="abrirModal('contact')">
              <span class="material-symbols-outlined">mail</span> Contacto
            </button>
          </li>
        </ul>

        <div class="sn-panel-footer">
          <a v-if="$root.constantes.tlf.iglesia" v-track:contacto="{ metodo: 'telefono', origen: 'menu_movil' }" :href="`tel:${$root.constantes.tlf.iglesia}`" class="sn-panel-quick">
            <span class="material-symbols-outlined">call</span> Llamar a la parroquia
          </a>
          <button type="button" class="sn-panel-quick" @click="toggleDark">
            <span class="material-symbols-outlined">{{ $store.getters.isDarkMode ? "light_mode" : "dark_mode" }}</span>
            {{ $store.getters.isDarkMode ? "Modo claro" : "Modo oscuro" }}
          </button>
        </div>
      </nav>
    </transition>

    <contact modalId="contact" />
    <evangelio-dia />
  </header>
</template>

<script>
import contact from "@/components/ContactoModal.vue";
import evangelioDia from "@/components/EvangelioDia.vue";
import { toggleTheme } from "@/directives/theme.js";
import { track } from "@/directives/analitica.js";
import { paginaVisible } from "@/contenido/config.js";

export default {
  name: "AppNavbar",
  components: {
    contact,
    evangelioDia,
  },
  data() {
    return {
      scrollY: 0,
      ticking: false,
      menuAbierto: false,
      desplegable: null, // "parroquia" | "caritas" | null
      cierreDesplegable: null,
      enlaces: [
        { name: "vistaNoticias", texto: "Noticias", icon: "newspaper" },
        { name: "Eventos", texto: "Eventos", icon: "calendar_month" },
        { name: "Multimedia", texto: "Galería", icon: "photo_library" },
      ],
    };
  },
  computed: {
    // Las páginas aún no publicadas solo se enlazan en desarrollo (src/contenido/config.js)
    desplegables() {
      const grupos = [
        {
          clave: "parroquia",
          texto: "Parroquia",
          items: [
            paginaVisible("historia") && { name: "historia", texto: "Historia y patrimonio", detalle: "El templo y su historia", icon: "account_balance" },
            paginaVisible("sanJudas") && { name: "sanJudas", texto: "San Judas Tadeo", detalle: "Devoción en la parroquia", icon: "local_fire_department" },
          ],
        },
        {
          clave: "caritas",
          texto: "Cáritas",
          items: [
            { name: "CaritasParroquialNoticias", texto: "Cáritas parroquial", detalle: "Noticias y actividades", icon: "volunteer_activism" },
            paginaVisible("caritasColabora") && { name: "caritasColabora", texto: "Colabora", detalle: "Donativos y voluntariado", icon: "favorite" },
            { href: "https://www.caritas.es/castrense/", texto: "Cáritas Castrense", detalle: "Web oficial", icon: "open_in_new" },
          ],
        },
      ];
      return grupos.map((g) => ({ ...g, items: g.items.filter(Boolean) })).filter((g) => g.items.length);
    },
    enPortada() {
      return this.$route.name === "inicio";
    },
    // En la portada, arriba del todo, la barra es transparente sobre la foto
    transparente() {
      return this.enPortada && this.scrollY < 60 && !this.menuAbierto;
    },
    compacta() {
      return this.scrollY > 60;
    },
    // Logo de texto claro: sobre la foto de portada o en modo oscuro
    logoClaro() {
      return this.transparente || this.$store.getters.isDarkMode;
    },
  },
  watch: {
    $route() {
      this.menuAbierto = false;
      this.desplegable = null;
    },
    menuAbierto(abierto) {
      document.body.style.overflow = abierto ? "hidden" : "";
    },
  },
  mounted() {
    this.scrollY = window.scrollY;
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onResize);
    document.addEventListener("keydown", this.onKeydown);
    document.addEventListener("click", this.onClickFuera);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("keydown", this.onKeydown);
    document.removeEventListener("click", this.onClickFuera);
    document.body.style.overflow = "";
    clearTimeout(this.cierreDesplegable);
  },
  methods: {
    toggleDark() {
      const oscuro = toggleTheme(this.$store);
      track("cambiar_tema", { tema: oscuro ? "oscuro" : "claro" });
    },
    activo(nombre) {
      return this.$route.name === nombre;
    },
    abrirModal(id) {
      this.menuAbierto = false;
      this.$bvModal.show(id);
    },
    abrirDesplegable(clave) {
      clearTimeout(this.cierreDesplegable);
      this.desplegable = clave;
    },
    cerrarDesplegableConRetardo() {
      clearTimeout(this.cierreDesplegable);
      this.cierreDesplegable = setTimeout(() => (this.desplegable = null), 180);
    },
    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.scrollY = window.scrollY;
        this.ticking = false;
      });
    },
    onResize() {
      if (window.innerWidth >= 992) this.menuAbierto = false;
    },
    onKeydown(e) {
      if (e.key === "Escape") {
        this.menuAbierto = false;
        this.desplegable = null;
      }
    },
    onClickFuera(e) {
      if (this.desplegable && !e.target.closest(".sn-drop")) this.desplegable = null;
    },
  },
};
</script>

<style scoped>
/* ---------- Barra ---------- */
.sn {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 1030;
  font-family: var(--sd-font-body);
  text-align: left;
  color: var(--sd-text);
  background: color-mix(in srgb, var(--sd-surface) 88%, transparent);
  border-bottom: 1px solid var(--sd-border);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
  backdrop-filter: saturate(160%) blur(14px);
  transition: background 0.3s, border-color 0.3s, box-shadow 0.3s;
}

.sn.is-compact {
  box-shadow: var(--sd-shadow);
}

/* Portada: transparente sobre la foto, con texto claro */
.sn.is-transparent {
  background: linear-gradient(180deg, rgba(11, 18, 33, 0.55) 0%, rgba(11, 18, 33, 0) 100%);
  border-bottom-color: transparent;
  box-shadow: none;
  backdrop-filter: none;
}

.sn-inner {
  display: flex;
  align-items: center;
  gap: 24px;
  max-width: 1280px;
  height: 96px;
  margin: 0 auto;
  padding: 0 20px;
  transition: height 0.3s;
}

.sn.is-compact .sn-inner {
  height: 76px;
}

.sn-brand {
  display: flex;
  flex-shrink: 0;
  align-items: center;
}

.sn-logo {
  width: auto;
  height: 68px;
  transition: height 0.3s;
}

.sn.is-compact .sn-logo {
  height: 56px;
}

/* ---------- Enlaces (escritorio) ---------- */
.sn-nav {
  flex: 1;
  display: flex;
  justify-content: center;
}

.sn-links {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.sn-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 44px;
  padding: 0 16px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  font-size: 1.06rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--sd-text);
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.sn-link:hover {
  background: var(--sd-surface-2);
  color: var(--sd-primary);
  text-decoration: none;
}

.sn-link::after {
  content: "";
  position: absolute;
  right: 14px;
  bottom: 4px;
  left: 14px;
  height: 2px;
  border-radius: 2px;
  background: var(--sd-gold);
  transform: scaleX(0);
  transition: transform 0.2s;
}

.sn-link.is-active {
  font-weight: 700;
  color: var(--sd-primary);
}

.sn-link.is-active::after {
  transform: scaleX(1);
}

.sn.is-transparent .sn-link {
  color: #fff;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.35);
}

.sn.is-transparent .sn-link:hover {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
}

.sn-link:focus-visible,
.sn-icon:focus-visible,
.sn-contact:focus-visible,
.sn-menu-item:focus-visible,
.sn-panel-link:focus-visible {
  outline: 2px solid var(--sd-gold);
  outline-offset: 2px;
}

.sn-caret {
  font-size: 20px;
  color: inherit;
  transition: transform 0.2s;
}

.sn-drop {
  position: relative;
}

.sn-link[aria-expanded="true"] .sn-caret {
  transform: rotate(180deg);
}

/* Desplegable */
.sn-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 260px;
  padding: 6px;
  border: 1px solid var(--sd-border);
  border-radius: 14px;
  background: var(--sd-surface);
  box-shadow: var(--sd-shadow-lg);
  transform: translateX(-50%);
}

.sn-menu::before {
  /* Puente invisible para no perder el hover entre el botón y el menú */
  content: "";
  position: absolute;
  top: -10px;
  right: 0;
  left: 0;
  height: 10px;
}

.sn-menu-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  color: var(--sd-text);
  text-decoration: none;
  transition: background 0.15s;
}

.sn-menu-item:hover {
  background: var(--sd-surface-2);
  color: var(--sd-text);
  text-decoration: none;
}

.sn-menu-item .material-symbols-outlined {
  margin-top: 1px;
  font-size: 22px;
  color: var(--sd-primary);
}

.sn-menu-item strong {
  display: block;
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--sd-text);
}

.sn-menu-item small {
  font-size: 0.78rem;
  color: var(--sd-muted);
}

.sn-pop-enter-active,
.sn-pop-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}

.sn-pop-enter,
.sn-pop-leave-to {
  opacity: 0;
  transform: translate(-50%, -6px);
}

/* ---------- Acciones ---------- */
.sn-actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
}

.sn-contact {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 44px;
  padding: 0 20px;
  border: 0;
  border-radius: 999px;
  background: var(--sd-primary);
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--sd-primary-contrast);
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;
}

.sn-contact:hover {
  background: var(--sd-primary-strong);
  transform: translateY(-1px);
}

.sn-contact span {
  color: inherit;
}

.sn-contact .material-symbols-outlined {
  font-size: 18px;
}

.sn.is-transparent .sn-contact {
  background: rgba(255, 255, 255, 0.16);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5);
  color: #fff;
  backdrop-filter: blur(6px);
}

.sn.is-transparent .sn-contact:hover {
  background: rgba(255, 255, 255, 0.26);
}

.sn-icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 1px solid var(--sd-border);
  border-radius: 50%;
  background: var(--sd-surface);
  color: var(--sd-text);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.sn-icon:hover {
  border-color: var(--sd-primary);
  color: var(--sd-primary);
}

.sn-icon .material-symbols-outlined {
  font-size: 22px;
  color: inherit;
}

.sn.is-transparent .sn-icon {
  border-color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.sn-burger {
  display: none;
}

/* ---------- Menú móvil ---------- */
.sn-overlay {
  position: fixed;
  inset: 0;
  z-index: 1;
  background: rgba(2, 6, 23, 0.5);
  backdrop-filter: blur(2px);
}

.sn-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  width: min(340px, 86vw);
  padding: calc(96px + env(safe-area-inset-top, 0px)) 16px calc(20px + env(safe-area-inset-bottom, 0px));
  overflow-y: auto;
  background: var(--sd-surface);
  box-shadow: -12px 0 40px rgba(0, 0, 0, 0.25);
}

.sn-panel-links {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.sn-panel-group {
  margin: 14px 0 2px;
  padding: 0 14px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--sd-muted);
}

.sn-panel-link {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 52px;
  padding: 0 14px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  font-size: 1.05rem;
  font-weight: 500;
  color: var(--sd-text);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}

.sn-panel-link:hover,
.sn-panel-link:active {
  background: var(--sd-surface-2);
  color: var(--sd-text);
  text-decoration: none;
}

.sn-panel-link .material-symbols-outlined {
  font-size: 22px;
  color: var(--sd-primary);
}

.sn-panel-link.is-active {
  background: var(--sd-primary-soft);
  font-weight: 600;
  color: var(--sd-primary);
}

.sn-panel-footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid var(--sd-border);
}

.sn-panel-quick {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  border: 1px solid var(--sd-border);
  border-radius: 12px;
  background: var(--sd-surface-2);
  font-weight: 600;
  color: var(--sd-text);
  text-decoration: none;
  cursor: pointer;
}

.sn-panel-quick:hover {
  color: var(--sd-primary);
  text-decoration: none;
}

.sn-panel-quick .material-symbols-outlined {
  font-size: 20px;
  color: var(--sd-primary);
}

.sn-fade-enter-active,
.sn-fade-leave-active {
  transition: opacity 0.25s;
}

.sn-fade-enter,
.sn-fade-leave-to {
  opacity: 0;
}

.sn-slide-enter-active,
.sn-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.sn-slide-enter,
.sn-slide-leave-to {
  transform: translateX(100%);
}

/* La barra y sus botones quedan por encima del panel móvil */
.sn-inner {
  position: relative;
  z-index: 3;
}

.sn.is-open {
  background: var(--sd-surface);
  backdrop-filter: none;
}

/* ---------- Responsive ---------- */
@media (max-width: 1280px) {
  .sn-link {
    padding: 0 12px;
  }
}

@media (max-width: 1100px) {
  .sn-link {
    padding: 0 8px;
    font-size: 0.98rem;
  }

  .sn-logo,
  .sn.is-compact .sn-logo {
    height: 56px;
  }

  .sn-contact span:last-child {
    display: none;
  }

  .sn-contact {
    width: 44px;
    padding: 0;
    justify-content: center;
  }
}

@media (max-width: 991px) {
  .sn-nav,
  .sn-contact {
    display: none;
  }

  .sn-burger {
    display: grid;
  }

  .sn-actions {
    margin-left: auto;
  }

  .sn-inner {
    height: 76px;
    padding: 0 14px;
  }

  .sn.is-compact .sn-inner {
    height: 66px;
  }

  .sn-logo {
    height: 52px;
  }

  .sn.is-compact .sn-logo {
    height: 46px;
  }

  .sn-panel {
    padding-top: calc(84px + env(safe-area-inset-top, 0px));
  }
}

@media (max-width: 380px) {
  .sn-logo {
    height: 44px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sn,
  .sn-inner,
  .sn-logo,
  .sn-slide-enter-active,
  .sn-slide-leave-active {
    transition: none;
  }
}
</style>
